import { useRef } from "react"
import { isAndroid, osVersion } from "react-device-detect"
import { CloseBlue, MagnifyingGlass } from "../../../common/atoms/Icons/Chat"
import { useMessagingView } from "../../hooks"
import { classNames } from "../../utils"
import styles from "./SearchInput.module.scss"

type Props = {
    className?: string
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>
    onClear?: () => void
}

export function SearchInput({ className, inputProps, onClear }: Props) {
    const messagingView = useMessagingView()
    const ref = useRef<HTMLInputElement>(null)

    const sanitizeUsername = (value: string) =>
        value.replace(/[^a-zA-Z0-9_]/g, "")

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let sanitizedValue = sanitizeUsername(e.target.value)
        if (e.target.maxLength && isAndroid && Number(osVersion) <= 13) {
            // maxlength workaround for samsung keyboards on android <= 13
            sanitizedValue = sanitizedValue.substring(0, e.target.maxLength)
        }
        e.target.value = sanitizedValue
        inputProps?.onChange?.(e)
    }

    // hack to fix cursor position on safari https://multimediallc.atlassian.net/browse/MSG-554
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        requestAnimationFrame(() => {
            const el = ref.current
            if (!el) return

            const len = el.value.length
            el.setSelectionRange(len, len)

            const style = window.getComputedStyle(el)
            const pr = parseFloat(style.paddingRight) || 0

            el.scrollLeft = el.scrollWidth - el.clientWidth + pr
        })
    }

    return (
        <div className={classNames(styles.filterInputWrapper, className)}>
            <MagnifyingGlass
                width={messagingView.isDesktopInbox ? "18" : "16"}
                height={messagingView.isDesktopInbox ? "18" : "16"}
            />
            <input
                ref={ref}
                type="text"
                className={classNames(styles.filterInput, {
                    [styles.hasClear]: !!onClear,
                    [styles.desktopInbox]: messagingView.isDesktopInbox,
                })}
                {...inputProps}
                onChange={handleChange}
                onFocus={handleFocus}
            />
            {onClear && (
                <span className={styles.clearButton} onClick={onClear}>
                    <CloseBlue height={24} width={24} />
                </span>
            )}
        </div>
    )
}
